// ── Juntar varios documentos de una persona en un solo PDF ──────────────────────
// Arma un único archivo con una portada (quién, qué documentos y en qué orden) seguida del
// contenido real de cada uno: las páginas de los PDF se copian tal cual y las imágenes
// jpg/png se insertan centradas en su propia página.
//
// Hoy lo usa la ventana de versiones de un documento (una versión vigente + las anteriores),
// pero la parte que fusiona es genérica a propósito: recibe título, etiquetas y lista de
// documentos, así que sirve igual para exportar cualquier otro conjunto de la ficha.
//
// jsPDF (ya usado en el resto del proyecto, ver pdfExporter.ts) solo puede CREAR páginas
// nuevas: no sabe leer un PDF existente para copiarle las páginas. Por eso esto usa
// pdf-lib, que sí puede copiar páginas de un PDF ya armado e insertar una imagen.
// pdf-lib se carga con import() dinámico, no estático: son ~200 KB que solo hacen falta
// cuando alguien pulsa "Exportar PDF". Importada arriba, engordaba el bundle inicial de
// toda la aplicación (medido: 1.16 MB → 1.36 MB gzip).
import type { PDFDocument as TipoPDFDocument } from 'pdf-lib';
import apiService from '../services/api';
import { claseEPP, CLASE_EPP_LABEL, type ClaseEPP } from './epp';
import { formatDate, formatRUT } from './formatters';

export interface ResultadoExportPDF {
  /** Cuántos documentos se incrustaron con éxito (PDF o imagen). */
  incluidos: number;
  /** Documentos que no se pudieron descargar o cuyo formato no se puede incrustar
   *  (ej. un .docx), con el motivo. Van igual en la portada, marcados aparte. */
  fallidos: { nombre: string; motivo: string }[];
}

export interface OpcionesExportPDF {
  persona: { nombre: string; rut: string };
  /** Encabezado de la portada, ej. "Historial de EPP". */
  titulo: string;
  /** Segunda línea opcional, ej. el nombre del documento cuyas versiones se exportan. */
  subtitulo?: string;
  /** Etiqueta de cada ítem en la portada. Por defecto, la clase de EPP si la tiene. */
  etiqueta?: (doc: any) => string | null;
  /** Nombre del archivo descargado, sin extensión. */
  nombreArchivo: string;
}

const A4: [number, number] = [595.28, 841.89];
const MARGEN = 40;

/** true si el content-type/nombre de archivo indica un PDF. */
const esPdf = (mime: string, nombre: string): boolean =>
  mime.includes('pdf') || /\.pdf$/i.test(nombre);

/** true si el content-type/nombre de archivo indica una imagen que pdf-lib sabe incrustar
 *  (jpg o png; otros formatos de imagen no tienen soporte nativo en la librería). */
const tipoImagen = (mime: string, nombre: string): 'jpg' | 'png' | null => {
  if (mime.includes('png') || /\.png$/i.test(nombre)) return 'png';
  if (mime.includes('jpeg') || mime.includes('jpg') || /\.(jpe?g)$/i.test(nombre)) return 'jpg';
  return null;
};

const nombreDe = (doc: any): string =>
  doc?.nombre_documento || doc?.nombre_original || `Documento ${doc?.id}`;

/** Fecha con la que se lista un documento: la de emisión si la trae, si no la de subida.
 *  Las versiones anteriores del historial vienen con fecha_creacion/created_at. */
const fechaDe = (doc: any): string | null =>
  doc?.fecha_emision || doc?.fecha_subida || doc?.fecha_creacion || doc?.created_at || null;

/** Etiqueta por defecto: la clase de EPP cuando el documento es uno, si no nada. */
const etiquetaPorDefecto = (doc: any): string | null => {
  const clase = claseEPP(doc) as ClaseEPP | null;
  return clase ? CLASE_EPP_LABEL[clase] : null;
};

const dibujarPortada = async (
  lib: typeof import('pdf-lib'),
  pdf: TipoPDFDocument,
  opciones: OpcionesExportPDF,
  docs: any[]
) => {
  const { StandardFonts, rgb } = lib;
  const font = await pdf.embedFont(StandardFonts.HelveticaBold);
  const fontRegular = await pdf.embedFont(StandardFonts.Helvetica);
  let page = pdf.addPage(A4);
  let y = A4[1] - MARGEN;

  const linea = (texto: string, size: number, bold = false, color = rgb(0.1, 0.1, 0.1)) => {
    if (y < MARGEN + 20) { page = pdf.addPage(A4); y = A4[1] - MARGEN; }
    page.drawText(texto, { x: MARGEN, y, size, font: bold ? font : fontRegular, color });
    y -= size + 8;
  };

  linea(opciones.titulo, 20, true);
  if (opciones.subtitulo) linea(opciones.subtitulo, 12, true, rgb(0.25, 0.25, 0.25));
  linea(`${opciones.persona.nombre}  ·  RUT ${formatRUT(opciones.persona.rut)}`, 12);
  linea(`Generado el ${formatDate(new Date().toISOString())}`, 9, false, rgb(0.4, 0.4, 0.4));
  y -= 10;
  linea(`${docs.length} documento${docs.length !== 1 ? 's' : ''}`, 11, true);
  y -= 4;

  const etiquetar = opciones.etiqueta || etiquetaPorDefecto;
  for (const d of docs) {
    const etiqueta = etiquetar(d);
    const fecha = fechaDe(d);
    linea(`•  ${etiqueta ? `[${etiqueta}] ` : ''}${nombreDe(d)}${fecha ? `  —  ${formatDate(fecha)}` : ''}`, 10);
  }
};

/**
 * Genera el PDF combinado y devuelve sus bytes junto con un resumen de qué se incluyó.
 * No descarga nada por sí sola: el llamador decide qué hacer con los bytes (ver
 * descargarDocumentosPDF más abajo, que además dispara la descarga en el navegador).
 */
export const generarDocumentosPDF = async (
  opciones: OpcionesExportPDF,
  docs: any[]
): Promise<{ bytes: Uint8Array; resultado: ResultadoExportPDF }> => {
  const lib = await import('pdf-lib');
  const { PDFDocument } = lib;
  const pdf = await PDFDocument.create();
  await dibujarPortada(lib, pdf, opciones, docs);

  const resultado: ResultadoExportPDF = { incluidos: 0, fallidos: [] };

  for (const doc of docs) {
    const nombre = nombreDe(doc);
    try {
      // visualizarDocumento no exige el permiso de descarga restringida (a diferencia de
      // downloadDocumento) y ya se usa para los mismos documentos en el botón "ojo".
      const { blob } = await apiService.visualizarDocumento(doc.id);
      const mime = (blob.type || doc.tipo_mime || '').toLowerCase();
      const nombreArchivo = doc.nombre_original || doc.nombre_archivo || '';
      const bytes = new Uint8Array(await blob.arrayBuffer());

      if (esPdf(mime, nombreArchivo)) {
        const origen = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const paginas = await pdf.copyPages(origen, origen.getPageIndices());
        paginas.forEach((p) => pdf.addPage(p));
        resultado.incluidos++;
        continue;
      }

      const tipoImg = tipoImagen(mime, nombreArchivo);
      if (tipoImg) {
        const imagen = tipoImg === 'png' ? await pdf.embedPng(bytes) : await pdf.embedJpg(bytes);
        const escala = Math.min((A4[0] - MARGEN * 2) / imagen.width, (A4[1] - MARGEN * 2) / imagen.height, 1);
        const w = imagen.width * escala;
        const h = imagen.height * escala;
        const page = pdf.addPage(A4);
        page.drawImage(imagen, { x: (A4[0] - w) / 2, y: (A4[1] - h) / 2, width: w, height: h });
        resultado.incluidos++;
        continue;
      }

      resultado.fallidos.push({ nombre, motivo: `Formato no soportado (${mime || 'desconocido'}); revisar el archivo original` });
    } catch (e: any) {
      resultado.fallidos.push({ nombre, motivo: e?.response?.status === 403 ? 'Sin permiso para verlo' : (e?.message || 'No se pudo descargar') });
    }
  }

  return { bytes: await pdf.save(), resultado };
};

/** Genera el PDF y dispara la descarga en el navegador. */
export const descargarDocumentosPDF = async (
  opciones: OpcionesExportPDF,
  docs: any[]
): Promise<ResultadoExportPDF> => {
  const { bytes, resultado } = await generarDocumentosPDF(opciones, docs);
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Los dos puntos y las barras rompen el nombre en Windows; el resto se deja tal cual
  // para que el archivo siga siendo legible.
  a.download = `${opciones.nombreArchivo.replace(/[\\/:*?"<>|]/g, '-')}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return resultado;
};

const rutParaArchivo = (rut: string): string => (rut || '').replace(/[^a-zA-Z0-9]/g, '');

/**
 * Un documento y sus versiones anteriores, en un solo PDF y en ese orden.
 * Se incluye la versión vigente a propósito: un PDF del historial sin el documento actual
 * obliga a exportar dos veces para tener la traza completa.
 */
export const descargarVersionesDocumentoPDF = (
  persona: { nombre: string; rut: string },
  actual: any,
  versiones: any[]
): Promise<ResultadoExportPDF> => {
  const nombre = nombreDe(actual);
  return descargarDocumentosPDF({
    persona,
    titulo: 'Historial de versiones',
    subtitulo: nombre,
    etiqueta: (d: any) => (d?.id === actual?.id ? 'Vigente' : 'Versión anterior'),
    nombreArchivo: `${nombre} - versiones - ${persona.nombre} (${rutParaArchivo(persona.rut)})`,
  }, [actual, ...versiones]);
};
